import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCan } from "@/auth/permissions";
import { listAttractions } from "@/services/attractions";
import { MediaTile } from "@/components/media/MediaTile";
import { MediaUploadModal } from "@/components/media/MediaUploadModal";
import { SubjectPicker, subjectReady, subjectToIds, type Subject } from "@/components/media/SubjectPicker";
import {
  deleteAsset, errMessage, getAsset, listLibrary, listPlaces, MEDIA_QK, reclassifyAsset, replaceAssetFile,
  type LibraryFilters, type MediaAsset, type MediaKind,
} from "@/services/mediaLibrary";
import { ApiError } from "@/services/api";
// ═ COMPANY-STRIP BEGIN ═
import { useEdition } from "@/theme/useTheme";
import { LicensedBatchBar, LicensedFilter, LicensedPicker, LicensedTag } from "@/pages/marketing/LicensedControls";   // MKT-1 · 实拍授权（订阅版）
// ═ COMPANY-STRIP END ═

/**
 * IMGLIB-1 · 图片库（新模块，侧栏「资源类 → 图片库」· 路由 /media-library）。
 * 契约：designs/mockups/image-library/图片库-样稿.html 第 1 页（tag image-library-mockup-v1）。
 * 全公司的产品图片都放这里；各产品从这里选图，同一张图可以给多条线用；在产品里上传的图也会自动进来。
 * 接口：GET /api/v1/media-library · GET/PATCH/DELETE /{id} · POST /{id}:replace · POST（上传）
 */
const sel = "rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";

export function MediaLibraryPage() {
  const qc = useQueryClient();
  const canUpload = useCan("products.products.update");
  const canManage = useCan("media.library.manage");
  const [place, setPlace] = useState("");
  const [attraction, setAttraction] = useState("");
  const [kind, setKind] = useState<"" | MediaKind>("");
  const [usage, setUsage] = useState<"" | "used" | "unused" | "unclassified">("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [sp] = useSearchParams();
  // 版本专属的额外筛选 / 卡片附加内容 / 抽屉附加内容（企业版恒为空 = 与改动前一致）
  const [extraFilter, setExtraFilter] = useState<Record<string, string>>({});
  let filterExtra: ReactNode = null;
  let tileExtra: (a: MediaAsset) => ReactNode = () => null;
  let drawerExtra: (id: string, a: MediaAsset) => ReactNode = () => null;
  let batchExtra: (ids: string[], total: number) => ReactNode = () => null;
  // ═ COMPANY-STRIP BEGIN ═
  const subscription = useEdition() === "subscription";
  const licensedQ = sp.get("licensed") ?? "";
  useEffect(() => { if (subscription && licensedQ) setExtraFilter({ licensed: licensedQ }); }, [subscription, licensedQ]);
  if (subscription) {
    filterExtra = <LicensedFilter className={sel} value={extraFilter.licensed ?? ""} onChange={(v) => setExtraFilter(v ? { licensed: v } : {})} />;
    tileExtra = (a) => <LicensedTag value={a.licensed} />;
    drawerExtra = (id, a) => <LicensedPicker assetId={id} value={a.licensed} canEdit={canUpload} />;
    batchExtra = (ids, total) => (canUpload ? <LicensedBatchBar assetIds={ids} total={total} /> : null);
  }
  // ═ COMPANY-STRIP END ═
  void sp;
  void setExtraFilter;

  const filters: LibraryFilters = useMemo(() => ({
    ...(place ? { place } : {}), ...(attraction ? { attraction } : {}), ...(kind ? { kind } : {}), ...(usage ? { usage } : {}), ...(q.trim() ? { q: q.trim() } : {}), ...extraFilter, limit: 200,
  }), [place, attraction, kind, usage, q, extraFilter]);
  const listQ = useQuery({ queryKey: MEDIA_QK.library(filters), queryFn: () => listLibrary(filters) });
  const placesQ = useQuery({ queryKey: MEDIA_QK.places, queryFn: listPlaces });
  const attractionsQ = useQuery({ queryKey: ["attractions", "by-place", place], enabled: !!place, queryFn: () => listAttractions({ place_id: place, limit: 200 }) });
  const tabs = listQ.data?.tabs;
  const items = listQ.data?.items ?? [];

  useEffect(() => { setAttraction(""); }, [place]);
  const refresh = () => qc.invalidateQueries({ queryKey: MEDIA_QK.libraryAll });
  const chip = (on: boolean, warn?: boolean) => `rounded-full border px-3 py-1 text-[13px] ${on ? "border-blue-600 bg-blue-600 font-semibold text-white" : warn ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`;

  return (
    <div className="flex flex-col gap-1 p-6 lg:p-8" data-testid="media-library-page">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-slate-900">图片库</h1>
        <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10.5px] font-bold text-white">新</span>
        {canUpload && (
          <button type="button" data-testid="media-upload-open" onClick={() => setUploadOpen(true)}
            className="ml-auto inline-flex items-center gap-1 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-blue-700">＋ 上传图片</button>
        )}
      </header>
      <p className="text-[13px] text-slate-500">全公司的产品图片都放这里。各产品从这里选图，同一张图可以给多条线用；在产品里上传的图也会自动进来。</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-[13px] text-slate-500">地点：
          <select aria-label="地点" className={sel} value={place} onChange={(e) => setPlace(e.target.value)}>
            <option value="">全部</option>
            {(placesQ.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name_cn}</option>)}
          </select></label>
        <label className="flex items-center gap-1.5 text-[13px] text-slate-500">景点：
          <select aria-label="景点" className={sel} value={attraction} disabled={!place} onChange={(e) => setAttraction(e.target.value)}>
            <option value="">{place ? "全部" : "先选地点"}</option>
            {(attractionsQ.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name_cn}</option>)}
          </select></label>
        <label className="flex items-center gap-1.5 text-[13px] text-slate-500">类型：
          <select aria-label="类型" className={sel} value={kind} onChange={(e) => setKind(e.target.value as "" | MediaKind)}>
            <option value="">全部</option><option value="gallery">相册（横版）</option><option value="highlight">宣传图（竖版）</option>
          </select></label>
        <label className="flex items-center gap-1.5 text-[13px] text-slate-500">使用情况：
          <select aria-label="使用情况" className={sel} value={usage} onChange={(e) => setUsage(e.target.value as "" | "used" | "unused" | "unclassified")}>
            <option value="">全部</option><option value="used">在用</option><option value="unused">没用上</option><option value="unclassified">未归类</option>
          </select></label>
        {filterExtra}
        <input aria-label="搜名字" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 搜名字，如「西雅图 太空针塔」"
          className="min-w-[220px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="media-chips">
        <button type="button" className={chip(!place && usage !== "unclassified")} onClick={() => { setPlace(""); setUsage(""); }}>全部 {tabs?.all ?? 0}</button>
        {(tabs?.places ?? []).map((p) => (
          <button key={p.place_id} type="button" className={chip(place === p.place_id)} onClick={() => { setPlace(p.place_id); setUsage(""); }}>{p.name} {p.count}</button>
        ))}
        <button type="button" data-testid="chip-unclassified" className={chip(usage === "unclassified", true)} onClick={() => { setPlace(""); setUsage("unclassified"); }}>⚠ 未归类 {tabs?.unclassified ?? 0}</button>
      </div>

      {listQ.data && batchExtra(items.map((a) => a.id), listQ.data.total)}
      <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div>
          {listQ.isLoading ? <p className="py-10 text-center text-sm text-slate-400">加载中…</p>
            : listQ.isError ? <button type="button" onClick={() => listQ.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button>
            : items.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400">没有符合条件的图片</p>
            : (
              <div className="grid grid-cols-2 items-start gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
                {items.map((a) => (
                  <MediaTile key={a.id} asset={a} active={selected === a.id} onClick={() => setSelected(a.id)}
                    sub={<>{a.usage_count > 0 ? `用在 ${a.usage_count} 条线` : "没用上"}{!a.classified && canUpload && <> · <span className="text-blue-600">去归类</span></>}</>}>
                    {tileExtra(a)}
                  </MediaTile>
                ))}
              </div>
            )}
          {listQ.data && listQ.data.total > items.length && <p className="mt-3 text-center text-[12px] text-slate-400">只显示前 {items.length} 张，共 {listQ.data.total} 张——用上面的筛选缩小范围</p>}
        </div>
        <Drawer assetId={selected} canUpload={canUpload} canManage={canManage} onClose={() => setSelected(null)} onChanged={refresh} onClassify={() => setClassifyOpen(true)} extra={drawerExtra} />
      </div>

      <MediaUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={refresh}
        defaultSubject={place ? { placeId: place, subject: attraction ? `att:${attraction}` : "" } : undefined} />
      {classifyOpen && selected && <ClassifyModal assetId={selected} onClose={() => setClassifyOpen(false)} onSaved={() => { setClassifyOpen(false); refresh(); }} />}
    </div>
  );
}

// ───────────────────────────── 右侧抽屉 ─────────────────────────────

function Drawer({ assetId, canUpload, canManage, onClose, onChanged, onClassify, extra }: {
  assetId: string | null; canUpload: boolean; canManage: boolean; onClose: () => void; onChanged: () => void; onClassify: () => void;
  extra: (id: string, a: MediaAsset) => ReactNode;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: MEDIA_QK.asset(assetId ?? ""), enabled: !!assetId, queryFn: () => getAsset(assetId!) });
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirm, setConfirm] = useState<"delete" | "replace" | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [inUse, setInUse] = useState<{ product_id: string; code: string; name_cn: string }[] | null>(null);
  useEffect(() => { setConfirm(null); setPendingFile(null); setErr(null); setInUse(null); }, [assetId]);

  const afterChange = () => { qc.invalidateQueries({ queryKey: MEDIA_QK.asset(assetId ?? "") }); onChanged(); };
  const delMut = useMutation({
    mutationFn: () => deleteAsset(assetId!),
    onSuccess: () => { setConfirm(null); onClose(); onChanged(); },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        const d = (e.detail as { detail?: { message?: string; products?: { product_id: string; code: string; name_cn: string }[] } } | undefined)?.detail;
        setInUse(d?.products ?? []); setErr(d?.message ?? "还有产品在用这张图，不能删；先到这些产品里「移出」");
      } else setErr(e instanceof Error ? e.message : "删除失败，请重试");
      setConfirm(null);
    },
  });
  const repMut = useMutation({
    mutationFn: () => replaceAssetFile(assetId!, pendingFile!),
    onSuccess: () => { setConfirm(null); setPendingFile(null); setErr(null); afterChange(); },
    onError: (e) => { setErr(errMessage(e, "替换失败，请重试")); setConfirm(null); },
  });

  if (!assetId) return <div className="hidden rounded-xl border border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-400 xl:block">点一张图，在这里看详情、改归类、替换文件</div>;
  const a = q.data;
  return (
    <aside className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-4 xl:self-start" data-testid="media-drawer">
      {q.isLoading || !a ? <p className="py-10 text-center text-sm text-slate-400">加载中…</p> : (
        <>
          <div className="flex items-start gap-2">
            <h4 className="min-w-0 flex-1 break-all text-[15px] font-semibold text-slate-900">{a.name}
              {a.qualified === true && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-medium text-emerald-600">合格</span>}
              {a.qualified === false && <span className="ml-2 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600">不合格</span>}
            </h4>
            <button type="button" aria-label="关闭详情" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={15} /></button>
          </div>
          <div className={`w-full overflow-hidden rounded-lg bg-slate-200 bg-cover bg-center ${a.kind === "highlight" ? "aspect-[3/4]" : "aspect-[3/2]"}`} style={{ backgroundImage: `url('${a.url}')` }} />
          <dl className="grid grid-cols-[64px_1fr] gap-x-3 gap-y-1.5 text-[13px]">
            <dt className="text-slate-500">地点</dt><dd className="font-medium text-slate-800">{a.place_name ?? <span className="text-amber-600">未归类</span>}</dd>
            <dt className="text-slate-500">景点</dt><dd className="font-medium text-slate-800">{a.attraction_name ?? a.category_label ?? <span className="text-amber-600">未归类</span>}</dd>
            <dt className="text-slate-500">类型</dt><dd className="font-medium text-slate-800">{a.kind_label}</dd>
            <dt className="text-slate-500">尺寸</dt><dd className="font-medium tabular-nums text-slate-800">{a.width && a.height ? `${a.width} × ${a.height}` : "未检测"}{a.bytes ? ` · ${(a.bytes / 1024 / 1024).toFixed(1)} MB` : ""}</dd>
            <dt className="text-slate-500">原文件</dt><dd className="text-slate-600">{a.original_filename ? `${a.original_filename}（备查）` : "—"}</dd>
            <dt className="text-slate-500">上传</dt><dd className="text-slate-600">{[a.created_at ? new Date(a.created_at).toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" }) : null, a.uploaded_by_name].filter(Boolean).join(" ") || "—"}</dd>
          </dl>
          {extra(a.id, a)}
          <div className="text-[13px] font-semibold text-slate-800">用在 {a.used_in.length} 条线</div>
          <div className="flex flex-col gap-1" data-testid="media-used-in">
            {a.used_in.length === 0 && <span className="text-[12.5px] text-slate-400">还没有产品在用</span>}
            {a.used_in.map((u, i) => (
              <Link key={`${u.product_id}-${u.slot}-${i}`} to={`/products/${u.product_id}?tab=content`} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-[12.5px] text-slate-700 hover:bg-blue-50">
                <span className="truncate">{u.code} {u.name_cn}</span>
                <em className="shrink-0 not-italic text-slate-400">{u.slot_label} · {u.is_cover ? "封面" : `第 ${u.position} 张`}</em>
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpload && <button type="button" data-testid="drawer-classify" onClick={onClassify} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-700 hover:border-blue-400">改归类</button>}
            {canManage && (
              <>
                <button type="button" data-testid="drawer-replace" onClick={() => fileRef.current?.click()} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-700 hover:border-blue-400">替换文件</button>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="drawer-replace-input"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setPendingFile(f); setConfirm("replace"); setErr(null); } }} />
                <button type="button" data-testid="drawer-delete" onClick={() => { setConfirm("delete"); setErr(null); setInUse(null); }} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-rose-600 hover:border-rose-300">删除</button>
              </>
            )}
          </div>
          {confirm === "replace" && pendingFile && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-800" role="alertdialog">
              替换文件 = {a.used_in.length} 条线一起换成新图（名字不变）。新文件：{pendingFile.name}
              <div className="mt-2 flex gap-2">
                <button type="button" data-testid="replace-confirm" disabled={repMut.isPending} onClick={() => repMut.mutate()} className="rounded-md bg-amber-600 px-3 py-1 text-[12.5px] font-medium text-white disabled:opacity-50">{repMut.isPending ? "替换中…" : "确认替换"}</button>
                <button type="button" onClick={() => { setConfirm(null); setPendingFile(null); }} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12.5px]">取消</button>
              </div>
            </div>
          )}
          {confirm === "delete" && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-800" role="alertdialog">
              确定删除「{a.name}」？{a.used_in.length > 0 ? `还有 ${a.used_in.length} 条线在用，删不掉；先到这些产品里「移出」。` : "删除后不能恢复。"}
              <div className="mt-2 flex gap-2">
                <button type="button" data-testid="delete-confirm" disabled={delMut.isPending} onClick={() => delMut.mutate()} className="rounded-md bg-rose-600 px-3 py-1 text-[12.5px] font-medium text-white disabled:opacity-50">确认删除</button>
                <button type="button" onClick={() => setConfirm(null)} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12.5px]">取消</button>
              </div>
            </div>
          )}
          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-700" role="alert" data-testid="drawer-error">
              {err}
              {inUse && inUse.length > 0 && <ul className="mt-1 list-disc pl-4">{inUse.map((p) => <li key={p.product_id}><Link className="underline" to={`/products/${p.product_id}?tab=content`}>{p.code} {p.name_cn}</Link></li>)}</ul>}
            </div>
          )}
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11.5px] text-slate-500">替换文件 = 在用的线一起换成新图（名字不变）。删除：还有产品在用，不能删；先到这些产品里「移出」。</p>
        </>
      )}
    </aside>
  );
}

// ───────────────────────────── 改归类 ─────────────────────────────

function ClassifyModal({ assetId, onClose, onSaved }: { assetId: string; onClose: () => void; onSaved: () => void }) {
  const q = useQuery({ queryKey: MEDIA_QK.asset(assetId), queryFn: () => getAsset(assetId) });
  const [subject, setSubject] = useState<Subject>({ placeId: "", subject: "" });
  const [err, setErr] = useState<string | null>(null);
  const [inited, setInited] = useState(false);
  useEffect(() => {
    const a = q.data;
    if (a && !inited) {
      setInited(true);
      setSubject({ placeId: a.place_id ?? "", subject: a.attraction_id ? `att:${a.attraction_id}` : a.category ? `cat:${a.category}` : "" });
    }
  }, [q.data, inited]);
  const save = useMutation({
    mutationFn: () => reclassifyAsset(assetId, subjectToIds(subject)),
    onSuccess: onSaved,
    onError: (e) => setErr(errMessage(e, "保存失败，请重试")),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label="改归类"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4" data-testid="media-classify-modal">
        <div className="mb-3 flex items-center justify-between"><h3 className="text-base font-semibold text-slate-900">改归类 · {q.data?.name ?? ""}</h3>
          <button type="button" aria-label="关闭" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button></div>
        <SubjectPicker value={subject} onChange={setSubject} />
        <p className="mt-3 text-[11.5px] text-slate-400">类型（相册 / 宣传图）不能改。归到新景点后，名字里的序号会接着那个景点已有的往下排。</p>
        {err && <p className="mt-2 text-sm text-rose-600" role="alert">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600">取消</button>
          <button type="button" data-testid="classify-save" disabled={!subjectReady(subject) || save.isPending} onClick={() => save.mutate()}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{save.isPending ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>
  );
}

export type { MediaAsset };
